/**
 * §P.5 fase 2 · La ficha de un examen: candidatos, rúbrica y diplomas.
 *
 * ## Los que no cumplen salen en la lista, y eso es el diseño
 *
 * `v_exam_eligibility` devuelve también los `is_eligible: false`, y el contrato
 * explica por qué no se esconden: «obligaría al maestro a salir de la pantalla para
 * inscribir al niño que estuvo dos meses escayolado, **que es justo el caso que la
 * vista no sabe juzgar**». Así que aparecen, con el motivo de por qué no cumplen, y
 * se pueden inscribir igual. La vista informa; decide el maestro.
 *
 * ## Se inscribe en lote
 *
 * «Doce peticiones son doce oportunidades de que la séptima falle y nadie sepa cuáles
 * llegaron». Se marcan los candidatos y se manda uno solo.
 *
 * ## La rúbrica cuelga del grado, así que hay que inscribir primero
 *
 * El `PUT` escribe sobre **los grados destino de los inscritos**, porque «Poomsae de
 * amarillo» se puntúa igual en diciembre y en junio. Consecuencia: sin nadie inscrito
 * no hay grados sobre los que escribir, y la pantalla lo dice en vez de enseñar un
 * formulario que no va a guardar nada.
 *
 * Y las plantillas globales (`is_own: false`) se leen y no se editan: las comparten
 * doscientos clubes. Guardar las propias del club **sustituye** a las globales de
 * esos grados.
 *
 * ## Retirar borra o retira, y no es lo mismo
 *
 * `deleted` si no había nada puntuado —«una inscripción que nunca llegó a examen no
 * es historial, es un apunte equivocado»— y `withdrawn` si ya había notas. La
 * pantalla dice cuál de las dos pasó, porque cambia si el nombre se queda o no.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import { abrirDocumento } from '@/shared/api/descargas';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta, euros, fecha, fechaYHora, numero } from '@/shared/ui/formato';
import { EditorDeRubrica } from '@/club/grados/EditorDeRubrica';
import {
  admiteInscripciones,
  admiteRubrica,
  ESTADOS_DE_EXAMEN,
  maximoPonderado,
  nombreDeCandidato,
  pastillaDeExamen,
  porQueNoCumple,
  resumenDeRequisitos,
  type CandidatoPosible,
  type CriterioDeExamen,
  type EstadoDeExamen,
  type Examen,
  type InscripcionDeExamen,
} from '@/club/grados/tiposDeExamen';
import { enlaceSeguro } from '@/shared/ui/enlaceSeguro';

/** Lo que devuelve cerrar el examen: cuántos aprobaron, suspendieron y no vinieron. */
interface ResumenDelCierre {
  readonly passed: number;
  readonly failed: number;
  readonly absent: number;
}

/** El orden del ciclo, para el selector de estado. */
const SIGUIENTES: Record<EstadoDeExamen, readonly EstadoDeExamen[]> = {
  draft: ['open', 'cancelled'],
  open: ['closed', 'cancelled'],
  closed: ['graded', 'open', 'cancelled'],
  graded: [],
  cancelled: [],
};

export function FichaDeExamen({
  club,
  examenId,
  puedeEditar,
  alCerrar,
  alAbrirAlumno,
}: {
  club: string;
  examenId: string;
  puedeEditar: boolean;
  alCerrar: () => void;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [problema, setProblema] = useState<Problema | null>(null);
  const [inscribiendo, setInscribiendo] = useState(false);
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [abierto, setAbierto] = useState<string | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [editandoRubrica, setEditandoRubrica] = useState(false);
  /*
   * Cerrar el examen: la clave de idempotencia de esta confirmación, o `null` si no
   * se está confirmando. Una clave por confirmación y no por pulsación: el doble
   * clic sobre «Sí, cerrar» es justo el que no puede dar dos veces los cinturones.
   */
  const [cerrando, setCerrando] = useState<string | null>(null);
  const [cerrado, setCerrado] = useState<ResumenDelCierre | null>(null);

  const invalidar = () => {
    void cache.invalidateQueries({ queryKey: ['examen', club, examenId] });
    void cache.invalidateQueries({ queryKey: ['inscritos', club, examenId] });
    void cache.invalidateQueries({ queryKey: ['elegibles', club, examenId] });
    void cache.invalidateQueries({ queryKey: ['examenes', club] });
  };

  const examen = useQuery({
    queryKey: ['examen', club, examenId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Examen;
    },
  });

  const inscritos = useQuery({
    queryKey: ['inscritos', club, examenId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .registrations.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as InscripcionDeExamen[];
    },
  });

  const criterios = useQuery({
    queryKey: ['criterios', club, examenId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .criteria.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as CriterioDeExamen[];
    },
  });

  const elegibles = useQuery({
    queryKey: ['elegibles', club, examenId],
    enabled: inscribiendo,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .eligible.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as CandidatoPosible[];
    },
  });

  /*
   * Calificar es **cerrar el examen** (`POST …/grade`): calcula las notas con lo
   * puntuado en la app del maestro, pone los cinturones y avisa a las familias.
   * Hasta el 13-10-2026 esto era un `PATCH` a `graded`, que dejaba el examen
   * «calificado» sin nada de eso y ya sin forma de calificarlo de verdad.
   */
  const cerrar = useMutation({
    mutationFn: async (clave: string) => {
      const r = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .grade.post({}, { headers: { 'idempotency-key': clave } });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResumenDelCierre;
    },
    onSuccess: (resumen) => {
      setCerrando(null);
      setCerrado(resumen);
      invalidar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiarEstado = useMutation({
    mutationFn: async (status: EstadoDeExamen) => {
      const r = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .patch({ status });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: invalidar,
    onError: (e) => setProblema(aProblema(e)),
  });

  const inscribir = useMutation({
    mutationFn: async (ids: readonly string[]) => {
      const r = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        // Un lote: ver la cabecera.
        .registrations.post({
          candidates: ids.map((student_id) => ({ student_id })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setInscribiendo(false);
      setMarcados(new Set());
      invalidar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .registrations({ registrationId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as { outcome: 'deleted' | 'withdrawn' };
    },
    onSuccess: (r) => {
      setProblema(
        r.outcome === 'withdrawn'
          ? {
              title: 'Retirado, y se queda en su historial',
              status: 200,
              detail:
                'Ya tenía notas, así que el examen ocurrió para él: la inscripción se marca como retirada en vez de borrarse.',
            }
          : null
      );
      invalidar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const verDiploma = async (inscripcionId: string) => {
    setDescargando(inscripcionId);
    const fallo = await abrirDocumento(
      `/v1/clubs/${club}/exam-registrations/${inscripcionId}/diploma.pdf`,
      opcionesDeSesion
    );
    setDescargando(null);
    if (fallo) setProblema(fallo);
  };

  if (examen.isPending) {
    return <Cargando filas={6} etiqueta="Cargando el examen" />;
  }

  if (examen.isError || !examen.data) {
    return (
      <PanelError
        problema={aProblema(examen.error)}
        reintentar={() => void examen.refetch()}
      />
    );
  }

  const e = examen.data;
  const pinta = ESTADOS_DE_EXAMEN[e.status];
  const lista = inscritos.data ?? [];
  const rubrica = criterios.data ?? [];
  const propios = rubrica.filter((c) => c.is_own);

  // ── Inscribir ────────────────────────────────────────────────────────────
  if (inscribiendo) {
    const candidatos = elegibles.data ?? [];
    const cumplen = candidatos.filter(
      (c) => c.is_eligible && !c.already_registered
    );
    const noCumplen = candidatos.filter(
      (c) => !c.is_eligible && !c.already_registered
    );

    const alternar = (id: string) =>
      setMarcados((m) => {
        const n = new Set(m);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      });

    const fila = (c: CandidatoPosible) => {
      const motivo = porQueNoCumple(c);
      return (
        <li key={c.student_id}>
          <label className="opcion">
            <input
              type="checkbox"
              checked={marcados.has(c.student_id)}
              // Sin siguiente grado no hay a qué examinarse.
              disabled={c.next_rank_id === null}
              onChange={() => alternar(c.student_id)}
            />
            <span>
              <strong>{nombreDeCandidato(c)}</strong>
              <em>
                {c.current_rank_name ?? 'sin grado'} →{' '}
                {c.next_rank_name ?? 'sin siguiente grado'} ·{' '}
                {Math.round(c.attendance_rate * 100)} % de asistencia
                {c.is_first_exam
                  ? ' · es su primer examen'
                  : ` · ${c.months_since_last} ${c.months_since_last === 1 ? 'mes' : 'meses'} desde el último grado`}
              </em>
            </span>
          </label>
          {/*
            El motivo, y no un simple «no cumple»: es lo que el maestro necesita para
            decidir si le inscribe de todas formas.
          */}
          {motivo && <p className="tenue">{motivo}</p>}
        </li>
      );
    };

    return (
      <section>
        <header className="titulo-fila">
          <h1>Inscribir en «{e.name}»</h1>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setInscribiendo(false);
              setMarcados(new Set());
            }}
          >
            Volver al examen
          </button>
        </header>

        {elegibles.isPending && (
          <Cargando filas={5} etiqueta="Calculando quién puede examinarse" />
        )}

        {elegibles.isError && (
          <PanelError
            problema={aProblema(elegibles.error)}
            reintentar={() => void elegibles.refetch()}
          />
        )}

        {problema && <PanelError problema={problema} />}

        {elegibles.data && (
          <>
            <h2 className="seccion">
              Cumplen los requisitos ({cumplen.length})
            </h2>
            {cumplen.length === 0 ? (
              <p className="tenue">
                Ninguno cumple ahora mismo. Mira la lista de abajo: puede que a
                alguien le falte poco y tenga sentido examinarle igual.
              </p>
            ) : (
              <ul className="candidatos">{cumplen.map(fila)}</ul>
            )}

            {/*
              Los que no cumplen, **visibles y apuntables**. Ver la cabecera: la
              vista no sabe juzgar al niño que estuvo dos meses escayolado.
            */}
            {noCumplen.length > 0 && (
              <>
                <h2 className="seccion">
                  No cumplen todavía ({noCumplen.length})
                </h2>
                <p className="tenue">
                  Salen aquí a propósito y se pueden inscribir igual. La lista
                  mira asistencia y meses; no sabe que uno estuvo escayolado ni
                  que otro lleva tres años y se lo merece.{' '}
                  <strong>Decides tú.</strong>
                </p>
                <ul className="candidatos">{noCumplen.map(fila)}</ul>
              </>
            )}

            <div className="acciones">
              <button
                type="button"
                disabled={marcados.size === 0 || inscribir.isPending}
                onClick={() => inscribir.mutate([...marcados])}
              >
                {inscribir.isPending
                  ? 'Inscribiendo…'
                  : marcados.size === 0
                    ? 'Marca a quién inscribes'
                    : `Inscribir a ${marcados.size}`}
              </button>
            </div>
          </>
        )}
      </section>
    );
  }

  // ── La ficha ─────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Exámenes
        </button>
        {puedeEditar && admiteInscripciones(e.status) && (
          <button type="button" onClick={() => setInscribiendo(true)}>
            Inscribir candidatos
          </button>
        )}
      </header>

      <div className="titulo-fila">
        <h1>{e.name}</h1>
        <span className={`pastilla ${pastillaDeExamen(e.status)}`}>
          {pinta?.etiqueta ?? e.status}
        </span>
      </div>

      <p className="tenue">{pinta?.explicacion}</p>

      <dl className="datos">
        <div>
          <dt>Cuándo</dt>
          <dd>{fechaYHora(e.scheduled_at)}</dd>
        </div>
        <div>
          <dt>Dónde</dt>
          <dd>{e.venue_name ?? 'Sin decidir'}</dd>
        </div>
        <div>
          <dt>Disciplina</dt>
          <dd>{e.discipline_name}</dd>
        </div>
        <div>
          <dt>Qué cuesta</dt>
          <dd>
            {e.fee_cents === null || e.fee_cents === 0
              ? 'Gratis'
              : euros(e.fee_cents)}
          </dd>
        </div>
        <div>
          <dt>Se cierra</dt>
          <dd>
            {e.registration_deadline
              ? fecha(e.registration_deadline)
              : 'Sin fecha límite'}
          </dd>
        </div>
        <div>
          <dt>Examinador</dt>
          <dd>{e.examiner_name ?? 'Sin asignar'}</dd>
        </div>
      </dl>

      {e.external_album_url && (
        <p className="tenue">
          {/*
            Enlace externo: las fotos no están en Kiap. `rel` completo porque es una
            URL que escribió el club y va a otra pestaña.
          */}
          Fotos del examen:{' '}
          <a
            href={enlaceSeguro(e.external_album_url)}
            target="_blank"
            rel="noreferrer noopener"
          >
            el álbum del club
          </a>
        </p>
      )}

      {problema && <PanelError problema={problema} />}

      {cerrado && (
        <div className="aviso aviso-calma" role="status">
          <strong>Examen cerrado.</strong>{' '}
          {[
            cuenta(cerrado.passed, 'aprobado', 'aprobados'),
            cuenta(cerrado.failed, 'suspenso', 'suspensos'),
            cerrado.absent > 0
              ? cuenta(cerrado.absent, 'no presentado', 'no presentados')
              : null,
          ]
            .filter(Boolean)
            .join(', ')}
          .
          {cerrado.passed > 0 &&
            ' Los aprobados ya llevan su cinturón nuevo y tienen su diploma.'}
        </div>
      )}

      {cerrando && (
        <div className="hueco hueco-confirmar">
          <h3>¿Cerrar el examen?</h3>
          <ul className="lista-clara">
            <li>
              Se calcula la nota de cada candidato con lo puntuado en la app del
              maestro.{' '}
              <strong>
                Quien no tenga ninguna nota queda como no presentado
              </strong>
              , no como suspenso.
            </li>
            <li>
              Los aprobados suben de cinturón, les sale el diploma y su familia
              recibe el aviso.
            </li>
            <li>
              <strong>No se puede deshacer.</strong> Si falta puntuar a alguien,
              hazlo antes desde la app.
            </li>
          </ul>
          <div className="acciones">
            <button
              type="button"
              disabled={cerrar.isPending}
              onClick={() => {
                setProblema(null);
                cerrar.mutate(cerrando);
              }}
            >
              {cerrar.isPending ? 'Cerrando…' : 'Sí, cerrar el examen'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={cerrar.isPending}
              onClick={() => setCerrando(null)}
            >
              Dejarlo
            </button>
          </div>
        </div>
      )}

      {/* El estado, con los pasos que de verdad tocan desde donde está. */}
      {puedeEditar && !cerrando && SIGUIENTES[e.status].length > 0 && (
        <div className="acciones">
          {SIGUIENTES[e.status].map((s) => (
            <button
              key={s}
              type="button"
              className={s === 'cancelled' ? 'btn-plano' : undefined}
              disabled={cambiarEstado.isPending}
              onClick={() => {
                setProblema(null);
                if (s === 'graded') setCerrando(crypto.randomUUID());
                else cambiarEstado.mutate(s);
              }}
            >
              {s === 'open'
                ? e.status === 'closed'
                  ? 'Volver a abrir las inscripciones'
                  : 'Abrir las inscripciones'
                : s === 'closed'
                  ? 'Cerrar la lista'
                  : s === 'graded'
                    ? 'Cerrar el examen y dar los cinturones'
                    : 'Cancelar el examen'}
            </button>
          ))}
        </div>
      )}

      <h2 className="seccion">Candidatos ({lista.length})</h2>

      {inscritos.isPending && (
        <Cargando filas={3} etiqueta="Cargando candidatos" />
      )}

      {lista.length === 0 ? (
        <p className="tenue">
          {admiteInscripciones(e.status)
            ? 'Nadie inscrito todavía. Kiap calcula quién cumple los requisitos de su cinturón.'
            : 'Nadie inscrito. Abre las inscripciones para poder apuntar candidatos.'}
        </p>
      ) : (
        <ul className="candidatos">
          {lista.map((i) => {
            const requisitos = resumenDeRequisitos(i.eligibility_snapshot);
            const desplegado = abierto === i.id;

            return (
              <li key={i.id}>
                <div className="aviso-cabeza">
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirAlumno(i.student_id)}
                  >
                    <strong>{nombreDeCandidato(i)}</strong>
                  </button>
                  <span className="pastilla pastilla-left">
                    {i.from_rank_name ?? 'sin grado'} → {i.to_rank_name}
                  </span>
                  {i.result && (
                    <span
                      className={`pastilla ${
                        i.result === 'pass' ? 'pastilla-bien' : 'pastilla-mal'
                      }`}
                    >
                      {i.result === 'pass' ? 'Aprobado' : 'No aprobado'}
                      {i.overall_score !== null
                        ? ` · ${Math.round(i.overall_score)} %`
                        : ''}
                    </span>
                  )}
                </div>

                <div className="aviso-pie">
                  <span className="tutor-acciones" style={{ margin: 0 }}>
                    {requisitos.length > 0 && (
                      <button
                        type="button"
                        className="enlace-fila"
                        aria-expanded={desplegado}
                        onClick={() => setAbierto(desplegado ? null : i.id)}
                      >
                        {desplegado
                          ? 'Ocultar cómo estaba al inscribirse'
                          : 'Cómo estaba al inscribirse'}
                      </button>
                    )}

                    {/* El diploma, solo si aprobó: es lo que va a la pared. */}
                    {i.result === 'pass' && (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={descargando === i.id}
                        onClick={() => void verDiploma(i.id)}
                      >
                        {descargando === i.id ? 'Abriendo…' : 'Ver el diploma'}
                      </button>
                    )}

                    {puedeEditar && admiteInscripciones(e.status) && (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={retirar.isPending}
                        onClick={() => retirar.mutate(i.id)}
                      >
                        Retirar
                      </button>
                    )}
                  </span>
                </div>

                {/*
                  El `eligibility_snapshot`: los requisitos **tal como estaban al
                  inscribir**. Es lo que hace que el examen de octubre siga
                  explicándose dentro de dos años aunque el club suba la exigencia.
                */}
                {desplegado && (
                  <dl className="datos">
                    {requisitos.map((r) => (
                      <div key={r.clave}>
                        <dt>{r.clave}</dt>
                        <dd>{r.valor}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h2 className="seccion">La rúbrica</h2>

      {criterios.isPending && (
        <Cargando filas={2} etiqueta="Cargando la rúbrica" />
      )}

      {/*
        La consecuencia de que la rúbrica cuelgue del grado: sin inscritos no hay
        grados destino sobre los que escribir. Dicho, no escondido.
      */}
      {lista.length === 0 ? (
        <p className="tenue">
          La rúbrica se define{' '}
          <strong>sobre los grados a los que se examina cada uno</strong>, no
          sobre el examen: «Poomsae de amarillo» se puntúa igual en diciembre y
          en junio. Así que primero inscribe a los candidatos y luego aparecerán
          aquí sus grados para puntuarlos.
        </p>
      ) : editandoRubrica ? (
        <EditorDeRubrica
          club={club}
          examenId={examenId}
          criterios={rubrica}
          /*
            Los grados destino de los inscritos, sin repetir: son los únicos sobre
            los que este examen puede escribir criterios.
          */
          grados={[
            ...new Map(
              lista.map((i) => [
                i.to_rank_id,
                { id: i.to_rank_id, nombre: i.to_rank_name },
              ])
            ).values(),
          ]}
          alCerrar={() => setEditandoRubrica(false)}
        />
      ) : rubrica.length === 0 ? (
        <>
          <p className="tenue">
            Todavía no hay criterios para estos grados, ni del club ni de
            plantilla.
          </p>
          {puedeEditar && admiteRubrica(e.status) && (
            <div className="acciones">
              <button type="button" onClick={() => setEditandoRubrica(true)}>
                Escribir la rúbrica
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="tenue">
            La nota final es un <strong>porcentaje del máximo ponderado</strong>{' '}
            y no una suma: así el listón no se mueve solo al añadir un criterio.
            Máximo ponderado ahora: {numero(maximoPonderado(rubrica), 2)}.
          </p>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Grado</th>
                  <th>Criterio</th>
                  <th>Apartado</th>
                  <th className="num">Máximo</th>
                  <th className="num">Peso</th>
                  <th>De quién</th>
                </tr>
              </thead>
              <tbody>
                {rubrica.map((c) => (
                  <tr key={c.id}>
                    <td>{c.rank_name ?? 'Todos'}</td>
                    <td>{c.label}</td>
                    <td>{c.category ?? '—'}</td>
                    <td className="num">{c.max_score}</td>
                    <td className="num">{c.weight}</td>
                    <td>
                      {/* Global no se toca: la comparten doscientos clubes. */}
                      <span
                        className={`pastilla ${c.is_own ? 'pastilla-info' : ''}`}
                      >
                        {c.is_own ? 'Del club' : 'Plantilla de Kiap'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {propios.length === 0 && (
            <p className="tenue">
              Todos vienen de las plantillas de Kiap. Si los editas, se guardan
              como del club y <strong>sustituyen</strong> a la plantilla en esos
              grados. Las globales no se tocan: las comparten doscientos clubes.
            </p>
          )}

          {puedeEditar && admiteRubrica(e.status) && (
            <div className="acciones">
              <button type="button" onClick={() => setEditandoRubrica(true)}>
                Editar la rúbrica
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
